import React, { useEffect, useState } from 'react'
import { logoutPlatformLegacy, useWistorAuth } from '../../api/auth'

/**
 * Avatar in the global header, opening a summary of the signed-in user.
 *
 * The `details` list below is the extension point: a new row is one entry, and
 * a future profile page gets its own item in the actions section at the bottom.
 */
export function V2UserMenu() {
  const auth = useWistorAuth()
  const [open, setOpen] = useState(false)

  useEffect(function closeOnEscape() {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (auth.status !== 'authorized') return null

  const { user } = auth
  const initial = (user.name || user.email || '?')[0].toUpperCase()

  // Same branching as the legacy header: SSO logout navigates away by itself,
  // the legacy one only clears local state so the page has to be reloaded.
  const handleLogout = async () => {
    if (auth.isSingleLogin) return auth.logout()
    logoutPlatformLegacy()
    window.location.reload()
  }

  const details: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Role',
      value: <span className={`v2-badge v2-badge--${user.role}`}>{user.role}</span>,
    },
    { label: 'Project', value: user.project?.title ?? '—' },
    { label: 'Sign-in', value: auth.isSingleLogin ? 'Single login (SSO)' : 'Wistor account' },
  ]

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="v2-avatar"
        onClick={() => setOpen(!open)}
        type="button"
        title={user.name || user.email}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {initial}
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setOpen(false)} />
          <div className="v2-more-dropdown" style={{ width: 280, right: 0, top: '100%', marginTop: 6, padding: 0 }}>

            {/* Identity */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 14px' }}>
              <div className="v2-avatar" style={{ width: 40, height: 40, fontSize: 15 }}>{initial}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {user.name}
                </div>
                <div style={{
                  fontSize: 12, color: 'var(--v2-text-tertiary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }} title={user.email}>
                  {user.email}
                </div>
              </div>
            </div>

            {/* Details */}
            <div style={{ borderTop: '1px solid var(--v2-border)', padding: '12px 16px' }}>
              {details.map(({ label, value }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, minHeight: 26, fontSize: 12,
                }}>
                  <span style={{ color: 'var(--v2-text-muted)' }}>{label}</span>
                  <span style={{
                    color: 'var(--v2-text-secondary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Actions — a profile page link belongs here once one exists */}
            <div style={{ borderTop: '1px solid var(--v2-border)', padding: 4 }}>
              <button className="v2-more-item" onClick={handleLogout} type="button">
                Log out
              </button>
            </div>

          </div>
        </>
      )}
    </div>
  )
}
